import React from 'react'
import styled, { css } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import {
    InvoiceResultDependencies,
    InvoiceResultLogic,
    PossibleColumns,
} from './logic'
import { VendorDisplay } from '~/features/ui-components/vendor-display'
import { useAppContext } from '~/app-context'
import { TransactionResult } from '~/features/transactions/ui/transaction-row'
import {
    BankTransaction,
    DocumentTransactionAssociation,
} from '~/storage/types'
import { Button } from '~common/components/button'
import { MatchSearch } from '~/features/match-search'
import { useCacheValue } from '~/cache-context'
import { TooltipComponent } from '~common/components/tooltip-component'
import {
    formatISODate,
    formatTimestampForDisplay,
} from '~common/utils/date-transformation/date-transformations'

const defaultVisibleColumns: PossibleColumns[] = [
    'match',
    'date',
    'amount',
    'vendorName',
    'invoiceId',
    'reason',
    'createdAt',
]

export function InvoiceResult(props: InvoiceResultDependencies) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(InvoiceResultLogic, {
        ...props,
        ...appContext,
    })

    // UI state from logic
    const isHovered = state.isHovered
    const hasIndividualToggle = React.useRef(false)

    // Cache values for reactivity
    const invoices = useCacheValue('invoices')
    const settings = useCacheValue('settings')
    const documentTransactionAssociations = useCacheValue(
        'documentTransactionAssociations',
    )
    const matchesByInvoice = useCacheValue('matchesByInvoice')
    const cacheLoadState = useCacheValue('loadState')

    const formattedData = (() => {
        const invoice = props.invoiceDbId ? invoices?.[props.invoiceDbId] : null
        return logic.formatInvoiceData(invoice, {
            specificMatchConfidence: props.specificMatchConfidence,
            referenceLocation: props.referenceLocation,
            contextTransactionId: props.contextTransactionId,
            dontShowMatches: props.dontShowMatches,
        })
    })()

    let specificConfidenceForMatch: number | undefined = undefined // If this items is referenced, what is the confidence to display
    const matchingTransactions: DocumentTransactionAssociation[] = [] // If this item has references
    let hasUserConfirmedMatch = false // If this item has a user confirmed match
    let hasAlreadyBeenMatchedToOtherTransaction = false // If this item has already been matched to another transaction

    if (cacheLoadState === 'success') {
        const assocIds = matchesByInvoice[props.invoiceDbId ?? ''] ?? []

        for (const assocId of assocIds) {
            const assoc = documentTransactionAssociations[assocId]
            matchingTransactions.push(assoc)
            if (assoc.method === 'user_confirmed') {
                if (assoc.transactionId !== props.contextTransactionId) {
                    hasAlreadyBeenMatchedToOtherTransaction = true
                }
                if (assoc.transactionId === props.contextTransactionId) {
                    hasUserConfirmedMatch = true
                }
            }
            if (assoc.method === 'auto' || assoc.method === 'user_confirmed') {
                if (assoc.transactionId === props.contextTransactionId) {
                    specificConfidenceForMatch = Math.round(assoc.confidence)
                }
            }
        }
    }

    const matchConfidenceToDisplay = specificConfidenceForMatch
    const handleChangeMatch = () => {
        if (props.invoiceDbId) {
            const invoice = invoices?.[props.invoiceDbId]
            if (invoice) {
                appContext.services.globalEvents.emit({
                    type: 'spawnPane',
                    render: () => (
                        <MatchSearch
                            rootItem={invoice}
                            itemType="invoice"
                            title="Find matching transactions for"
                        />
                    ),
                    title: 'Find matching transactions for',
                    matchSearchProps: {
                        rootItem: invoice,
                        itemType: 'invoice',
                        title: 'Find matching transactions for',
                    },
                })
            }
        }
    }

    const handleMouseEnter = () => logic.setHovered(true)
    const handleMouseLeave = () => logic.setHovered(false)

    // Use local state if user has individually toggled, otherwise use global state if provided
    const showConfirmedMatches =
        props.showAllConfirmedMatches === undefined ||
        hasIndividualToggle.current
            ? state.showConfirmedMatches
            : props.showAllConfirmedMatches

    const renderReferencedTransaction = () => {
        const referenced = props.referenceLocation != null
        if (
            (props.referenceLocation === 'transactions' ||
                props.dontShowMatches ||
                !matchingTransactions) &&
            props.referenceLocation != 'emails'
        ) {
            return null
        }

        if (cacheLoadState === 'running') {
            return (
                <SubTransactionRow>
                    <TableCell referenced={referenced} colSpan={6}>
                        <NoData>Loading matching transactions...</NoData>
                    </TableCell>
                </SubTransactionRow>
            )
        }
        if (cacheLoadState === 'success' && matchingTransactions?.length > 0) {
            const shouldHideConfirmed =
                hasUserConfirmedMatch && !showConfirmedMatches

            if (shouldHideConfirmed) {
                return null
            }

            const sortedMatchingTransactions = matchingTransactions.sort(
                (a, b) => b.confidence - a.confidence,
            )

            const userConfirmedMatches = sortedMatchingTransactions.filter(
                (assoc) => assoc.method === 'user_confirmed',
            )

            const subRows: React.ReactNode[] = []

            if (userConfirmedMatches.length > 0 && !showConfirmedMatches) {
                return null
            }

            const renderTransactionResult = (
                key: string,
                transactionId: string,
                confidence: number,
            ) => {
                const visibleColumns = props.visibleColumns?.filter(
                    (column) => column !== 'createdAt',
                ) ?? [
                    'match',
                    'date',
                    'amount',
                    'vendorName',
                    'referenceText',
                    'note',
                ]

                return (
                    <TransactionResult
                        key={key}
                        transactionDbId={transactionId}
                        isReferenced={true}
                        visibleColumns={visibleColumns}
                        hideAvatar={props.hideAvatar}
                        onClick={() => {}}
                        specificMatchConfidence={confidence}
                        contextInvoiceId={props.invoiceDbId || undefined}
                    />
                )
            }

            if (userConfirmedMatches.length > 0) {
                userConfirmedMatches.forEach(
                    ({ transactionId, confidence }) => {
                        subRows.push(
                            renderTransactionResult(
                                `${props.invoiceDbId}-${transactionId}`,
                                transactionId,
                                confidence,
                            ),
                        )
                    },
                )
            }

            const autoMatches = sortedMatchingTransactions.filter(
                (assoc) => assoc.method === 'auto',
            )

            const topMatch = autoMatches[0]
            const restMatches = autoMatches.slice(1)

            if (
                topMatch &&
                (state.showAllMatches || userConfirmedMatches.length === 0)
            ) {
                subRows.push(
                    renderTransactionResult(
                        `${props.invoiceDbId}-${topMatch.transactionId}`,
                        topMatch.transactionId,
                        topMatch.confidence,
                    ),
                )
            }
            if (restMatches.length > 0 && state.showAllMatches) {
                restMatches.forEach(({ transactionId, confidence }) => {
                    subRows.push(
                        renderTransactionResult(
                            `${props.invoiceDbId}-${transactionId}`,
                            transactionId,
                            confidence,
                        ),
                    )
                })
            }

            if (
                autoMatches.length > 1 ||
                (topMatch && userConfirmedMatches.length > 0)
            ) {
                const buttonText = state.showAllMatches
                    ? 'Hide all matches'
                    : 'Show All Matches'

                const matchConfidence = Math.round(topMatch.confidence)
                let matchText = `${matchConfidence}% Confirm Match`

                if (topMatch.method === 'user_confirmed') {
                    matchText = 'Match Confirmed'
                }

                const transactionId = topMatch.transactionId
                const invoiceDbId = topMatch.documentId

                subRows.push(
                    <ButtonRowTransactionMatches
                        location={props.referenceLocation}
                    >
                        <MoreResultsCell referenced={referenced} colSpan={6}>
                            {props.referenceLocation === 'emails' &&
                            invoiceDbId &&
                            transactionId ? (
                                <Button
                                    label={matchText}
                                    variant="secondary"
                                    size="small"
                                    onClick={() =>
                                        logic.confirmMatch(
                                            invoiceDbId,
                                            transactionId,
                                        )
                                    }
                                />
                            ) : (
                                <Button
                                    label={buttonText}
                                    variant="ghost"
                                    size="small"
                                    onClick={() => logic.toggleShowAllMatches()}
                                />
                            )}
                        </MoreResultsCell>
                    </ButtonRowTransactionMatches>,
                )
            }

            return subRows
        }

        if (cacheLoadState === 'error') {
            return (
                <SubTransactionRow>
                    <TableCell referenced={referenced} colSpan={6}>
                        <NoData>Error loading matching transactions</NoData>
                    </TableCell>
                </SubTransactionRow>
            )
        }

        return null
    }

    const matchCellContent = () => {
        const isReferenced =
            props.referenceLocation != null ||
            props.contextTransactionId != null
        const confirmedButton = () => {
            return (
                <Button
                    label="Matched"
                    icon="check-circle"
                    onClick={(e) => {
                        e.stopPropagation()
                        if (e.shiftKey && props.onToggleAllConfirmedMatches) {
                            props.onToggleAllConfirmedMatches()
                            // Reset individual toggle when using global toggle
                            hasIndividualToggle.current = false
                        } else {
                            hasIndividualToggle.current = true
                            logic.toggleShowConfirmedMatches()
                        }
                    }}
                    variant="ghost"
                    size="small"
                    status="info"
                    tooltip={
                        !showConfirmedMatches
                            ? `Match Confirmed!\nClick to view\nShift+Click to toggle all`
                            : `Click to hide\nShift+Click to toggle all`
                    }
                />
            )
        }

        const confirmMatchButton = (warning?: boolean) => {
            if (props.invoiceDbId && props.contextTransactionId) {
                return (
                    <Button
                        onClick={(e) => {
                            e.stopPropagation()
                            logic.confirmMatch(
                                props.invoiceDbId!,
                                props.contextTransactionId!,
                            )
                        }}
                        icon="check"
                        variant="secondary"
                        size="small"
                        status={warning ? 'warning' : undefined}
                        tooltip={
                            warning
                                ? 'Caution! \nAlready matched to other transaction'
                                : 'Confirm match'
                        }
                    />
                )
            }
        }

        const removeMatchSuggestionButton = () => {
            if (props.invoiceDbId && props.contextTransactionId) {
                return (
                    <Button
                        icon="close"
                        tooltip="Remove match suggestion"
                        onClick={(e) => {
                            e.stopPropagation()
                            logic.removeMatch(
                                props.contextTransactionId!,
                                props.invoiceDbId!,
                            )
                        }}
                        variant="secondary"
                        size="small"
                    />
                )
            }
        }

        const confidenceDisplay = (align?: 'left' | 'center') => {
            if (matchConfidenceToDisplay === undefined) {
                return null
            }
            return (
                <ConfidenceDisplay align={align}>
                    {matchConfidenceToDisplay}%
                </ConfidenceDisplay>
            )
        }

        const findMatchButton = () => {
            return (
                <Button
                    label="Find Match"
                    onClick={(e) => {
                        e.stopPropagation()
                        handleChangeMatch()
                    }}
                    variant="secondary"
                    icon="search"
                    size="small"
                />
            )
        }

        const removeMatchButton = () => {
            if (props.invoiceDbId && props.contextTransactionId) {
                return (
                    <Button
                        icon="close"
                        tooltip="Remove Match"
                        onClick={() => {
                            appContext.services.transactionInvoiceMatching?.resetAssociationToAuto(
                                props.contextTransactionId!,
                                props.invoiceDbId!,
                            )
                        }}
                        variant="secondary"
                        size="small"
                    />
                )
            } else {
                throw new Error('Invoice ID and transaction ID are required')
            }
        }

        const changeMatchButton = () => {
            if (props.contextTransactionId) {
                return (
                    <Button
                        icon="search"
                        tooltip="Change"
                        onClick={() => {
                            if (!props.contextTransactionId) return

                            const rootItemTransaction =
                                appContext.services.cache.getKey(
                                    'transactions',
                                )?.[props.contextTransactionId]

                            appContext.services.globalEvents.emit({
                                type: 'spawnPane',
                                render: () => (
                                    <MatchSearch
                                        rootItem={
                                            rootItemTransaction as BankTransaction
                                        }
                                        itemType="transaction"
                                        title="Find matching transactions for"
                                    />
                                ),
                                title: 'Find matching transactions for',
                            })
                        }}
                        variant="secondary"
                        size="small"
                    />
                )
            }
        }

        if (hasUserConfirmedMatch) {
            if (isReferenced) {
                if (isHovered) {
                    return (
                        <ButtonRow>
                            {removeMatchButton()}
                            {changeMatchButton()}
                        </ButtonRow>
                    )
                } else {
                    if (props.context !== 'match-search') {
                        return (
                            <ButtonRow>{confidenceDisplay('center')}</ButtonRow>
                        )
                    } else {
                        return <ButtonRow>{confirmedButton()}</ButtonRow>
                    }
                }
            } else {
                return <ButtonRow>{confirmedButton()}</ButtonRow>
            }
        } else {
            if (isReferenced) {
                if (isHovered) {
                    if (props.context == 'match-search') {
                        return (
                            <ButtonRow>
                                {confirmMatchButton(
                                    hasAlreadyBeenMatchedToOtherTransaction,
                                )}
                                {removeMatchSuggestionButton()}
                            </ButtonRow>
                        )
                    }
                    return (
                        <ButtonRow>
                            {confirmMatchButton(
                                hasAlreadyBeenMatchedToOtherTransaction,
                            )}
                            {removeMatchSuggestionButton()}
                            {confidenceDisplay()}
                        </ButtonRow>
                    )
                }
                return <ButtonRow>{confidenceDisplay('center')}</ButtonRow>
            } else if (hasAlreadyBeenMatchedToOtherTransaction) {
                return <ButtonRow>{confirmedButton()}</ButtonRow>
            } else {
                if (isHovered) {
                    return findMatchButton()
                } else {
                    return undefined
                }
            }
        }
    }

    const renderCell = (field: PossibleColumns) => {
        if (formattedData === null) {
            return null
        }

        const isReferenced = props.referenceLocation != null
        const isTxnRef = props.referenceLocation === 'transactions'

        switch (field) {
            case 'match':
                if (props.dontShowMatches) {
                    return null
                }
                return (
                    <TableCell key={field} referenced={isReferenced}>
                        {matchCellContent()}
                    </TableCell>
                )
            case 'date':
                return (
                    <TableCell key={field} referenced={isReferenced}>
                        <Value bold>{formattedData.date}</Value>
                    </TableCell>
                )
            case 'amount':
                return (
                    <TableCell key={field} referenced={isReferenced}>
                        <TableCellContent>
                            <Currency>{formattedData.currency}</Currency>
                            <Value bold>{formattedData.amount}</Value>
                        </TableCellContent>
                    </TableCell>
                )
            case 'vendorName':
                return (
                    <TableCell key={field} referenced={isReferenced}>
                        {formattedData.vendorEmail ? (
                            <VendorDisplay
                                vendorName={formattedData.vendorName}
                                vendorEmail={formattedData.vendorEmail}
                                hideEmail={true}
                                hideAvatar={props.hideAvatar}
                            />
                        ) : (
                            <Value bold>{formattedData.vendorName}</Value>
                        )}
                    </TableCell>
                )
            case 'invoiceId':
                if (props.referenceLocation != null) {
                    return null
                }
                return (
                    <TableCell key={field} referenced={isReferenced}>
                        <Value>{formattedData.invoiceId}</Value>
                    </TableCell>
                )
            case 'reason':
                return (
                    <TableCell
                        key={field}
                        referenced={isReferenced}
                        colSpan={isTxnRef ? 2 : 1}
                    >
                        <TableCellContent>
                            {isTxnRef && (
                                <Value dontOverflow={true}>
                                    {formattedData.invoiceId} -{' '}
                                </Value>
                            )}
                            <Value>{formattedData.reason}</Value>
                        </TableCellContent>
                    </TableCell>
                )
            case 'createdAt':
                return (
                    <TableCell key={field} referenced={isReferenced}>
                        <TooltipComponent
                            content={(() => {
                                const date = new Date(formattedData.createdAt)
                                const formattedDate = formatISODate(
                                    date.toISOString(),
                                    settings?.dateFormat ?? 'MM/DD/YYYY',
                                )
                                const hours = String(date.getHours()).padStart(
                                    2,
                                    '0',
                                )
                                const minutes = String(
                                    date.getMinutes(),
                                ).padStart(2, '0')
                                const seconds = String(
                                    date.getSeconds(),
                                ).padStart(2, '0')
                                return `${formattedDate} ${hours}:${minutes}:${seconds}`
                            })()}
                        >
                            <Value>
                                {formatTimestampForDisplay(
                                    formattedData.createdAt,
                                    settings?.dateFormat ?? 'MM/DD/YYYY',
                                )}
                            </Value>
                        </TooltipComponent>
                    </TableCell>
                )
            case 'category':
                if (!props.visibleColumns?.includes('category')) {
                    return null
                }
                return (
                    <TableCell
                        key={field}
                        referenced={isReferenced}
                    ></TableCell>
                )
            case 'currency':
            case 'matchConfidence':
            case 'referenceText':
            case 'note':
                return null
            default:
                return null
        }
    }

    const visibleColumns = props.visibleColumns || defaultVisibleColumns

    return (
        <>
            <TableRowContainer
                onClick={logic.handleClickOnResult}
                referenced={props.referenceLocation != null}
                confirmedMatch={hasUserConfirmedMatch}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                key={
                    props.invoiceDbId + '-' + (props.contextTransactionId ?? '')
                }
            >
                {visibleColumns.map((field) => renderCell(field))}
            </TableRowContainer>
            {props.context !== 'match-search' && renderReferencedTransaction()}
        </>
    )
}

const SubTransactionRow = styled.tr<{
    onMouseEnter?: () => void
    onMouseLeave?: () => void
}>`
    background: ${({ theme }) => theme.colors.background.bg1};
    td {
        font-size: ${({ theme }) => theme.typography.fontSize.xs};
        margin-bottom: 20px;
    }
    ${(props) =>
        props.onMouseEnter &&
        css`
            &:hover {
                cursor: pointer;
            }
        `}// Optional hover style
`

const Value = styled.div<{
    bold?: boolean
    width?: string
    dontOverflow?: boolean
}>`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme, bold }) =>
        bold
            ? theme.typography.fontWeight.medium
            : theme.typography.fontWeight.light};
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    ${({ width }) => width && `width: ${width};`}

    ${({ dontOverflow }) =>
        dontOverflow &&
        css`
            overflow: visible;
        `}
`

const NoData = styled.div`
    color: ${({ theme }) => theme.colors.text.tertiary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    text-align: center;
    padding: ${({ theme }) => theme.spacing[2]};
`

const Currency = styled.div`
    color: ${({ theme }) => theme.colors.greyScale[100]};
    width: 10px;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

// Table row components for table layout
const TableRowContainer = styled.tr<{
    referenced?: boolean
    onClick?: () => void
    context?: 'match-search'
    confirmedMatch?: boolean
}>`
    width: 100%;
    height: ${({ theme }) => theme.spacing[16]};

    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};

    ${({ referenced }) =>
        referenced &&
        css`
            border: none;
            border-radius: 0 0 ${({ theme }) => theme.borderRadius.lg};
            border-top: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
            background: ${({ theme }) => theme.colors.background.bg2};
            height: ${({ theme }) => theme.spacing[12]};
            border-spacing: 0;
        `}
    ${({ referenced }) =>
        !referenced &&
        css`
            border-radius: ${({ theme }) => theme.borderRadius.lg};
        `}
${({ onClick }) =>
        onClick &&
        css`
            cursor: pointer;
            &:hover {
                background: ${({ theme }) => theme.colors.background.bg2};
            }
        `}

        ${({ referenced, confirmedMatch }) =>
        referenced &&
        confirmedMatch &&
        css`
            box-sizing: border-box;
            opacity: 0.6;
        `}
`

const TableCell = styled.td<{ referenced?: boolean }>`
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    vertical-align: middle;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${({ referenced }) =>
        referenced &&
        css`
            border: none;
            background: ${({ theme }) => theme.colors.background.bg2}90;
        `}
`

const MoreResultsCell = styled.td<{ referenced?: boolean }>`
    vertical-align: middle;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TableCellContent = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.spacing[1]};
`

const ConfidenceDisplay = styled.span<{ align?: 'left' | 'center' }>`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-right: 8px;
    padding-left: ${({ theme }) => theme.spacing[4]};
`

const ButtonRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
`

const ButtonRowTransactionMatches = styled.tr<{ location?: string }>`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    margin-top: ${({ theme }) => theme.spacing[2]};
    margin-left: ${({ theme }) => theme.spacing[2]};

    ${({ location }) =>
        location !== 'emails' &&
        css`
            margin-bottom: ${({ theme }) => theme.spacing[4]};
        `}
`
